import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, X, FileText, Eye, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { apiCache } from '../../services/apiCache';
import EnquiryDetailsModal from './EnquiryDetailsModal';
import AssignmentCompletionWizard from './AssignmentCompletionWizard';

interface ActiveAssignment {
  id: number;
  enquiry_id: number;
  agency_name: string;
  travel_date: string;
  plan_text: string;
  started_at: string;
  status?: string;
  pause_reason?: string;
  number_of_adults?: number;
  number_of_kids?: number;
  number_of_nights?: number;
  changes_requested?: string;
  is_rework?: boolean;
  enquiry_request_id?: number;
  priority?: string;
  attachment_file_path?: string;
  attachment_file_name?: string;
}

const WorkWidget: React.FC = () => {
  const [activeAssignments, setActiveAssignments] = useState<ActiveAssignment[]>([]);
  const [currentAssignment, setCurrentAssignment] = useState<ActiveAssignment | null>(null);
  const [availableItineraries, setAvailableItineraries] = useState<any[]>([]);
  const [linkedItineraries, setLinkedItineraries] = useState<any[]>([]);
  const [showCompletionWizard, setShowCompletionWizard] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAssignmentList, setShowAssignmentList] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState<{assignment: ActiveAssignment, reason: string} | null>(null);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [lastUpdated, setLastUpdated] = useState<number>(0);
  const isFetchingRef = React.useRef(false);

  const checkActiveAssignment = React.useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    
    try {
      console.log('JB NEW - WorkWidget checking active assignments');
      const response = await api.get('/my-assignments/active-all');
      const activeAssignments = response.data.success ? response.data.data : [];
      console.log('JB NEW - WorkWidget active assignments:', activeAssignments);
      
      if (activeAssignments && activeAssignments.length > 0) {
        setActiveAssignments(activeAssignments);
        
        // Update current assignment with fresh data
        setCurrentAssignment(prev => {
          if (prev) {
            const updatedCurrent = activeAssignments.find((a: ActiveAssignment) => a.id === prev.id);
            if (updatedCurrent) {
              console.log('JB NEW - Current assignment updated:', updatedCurrent);
              console.log('JB NEW - Pause reason:', updatedCurrent.pause_reason);
              return updatedCurrent;
            }
          }
          console.log('JB NEW - Current assignment set to:', activeAssignments[0]);
          return activeAssignments[0];
        });
        
        setLastUpdated(Date.now());
      } else {
        console.log('JB NEW - No active assignments found');
        setActiveAssignments([]);
        setCurrentAssignment(null);
      }
    } catch (error) {
      console.error('JB NEW - Error checking active assignments:', error);
      setActiveAssignments([]);
      setCurrentAssignment(null);
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    checkActiveAssignment();
    const interval = setInterval(checkActiveAssignment, 120000); // Check every 2 minutes
    return () => clearInterval(interval);
  }, [checkActiveAssignment]);

  const handlePauseWork = () => {
    setShowPauseModal(true);
    setPauseReason('');
  };

  const handleConfirmPause = async () => {
    if (!currentAssignment) return;
    
    try {
      await api.post(`/work-assignments/${currentAssignment.id}/pause`, {
        reason: pauseReason || 'No reason provided'
      });
      
      await checkActiveAssignment();
      setShowPauseModal(false);
      setPauseReason('');
    } catch (error) {
      console.error('Error pausing assignment:', error);
      alert('Failed to pause assignment');
    }
  };

  const handleResumeWork = async () => {
    if (!currentAssignment) return;
    
    try {
      await api.post(`/work-assignments/${currentAssignment.id}/resume`);
      await checkActiveAssignment();
    } catch (error) {
      console.error('Error resuming assignment:', error);
      alert('Failed to resume assignment');
    }
  };

  const handleCompleteWork = async () => {
    if (!currentAssignment) return;
    
    try {
      console.log('WorkWidget handleCompleteWork called with currentAssignment:', currentAssignment);
      
      // Get unassigned itineraries for this customer
      const response = await api.get(`/itineraries/customer/${currentAssignment.enquiry_id}/unassigned`);
      console.log('WorkWidget unassigned itineraries response:', response.data);
      
      if (response.data.success) {
        setAvailableItineraries(response.data.data || []);
        setShowCompletionWizard(true);
      } else {
        console.log('WorkWidget - No itineraries found');
        setAvailableItineraries([]);
        setShowCompletionWizard(true);
      }
    } catch (error) {
      console.error('WorkWidget: Error fetching itineraries:', error);
      setAvailableItineraries([]);
      setShowCompletionWizard(true);
    }
  };

  const handleSwitchAssignment = (assignment: ActiveAssignment) => {
    if (currentAssignment?.id === assignment.id) {
      setShowAssignmentList(false);
      return;
    }
    
    // Show confirmation modal
    setShowSwitchConfirm({ assignment, reason: '' });
    setShowAssignmentList(false);
  };

  const confirmSwitch = async () => {
    if (!showSwitchConfirm || !currentAssignment) return;
    
    // Validate reason is provided
    if (!showSwitchConfirm.reason.trim()) {
      alert('Please provide a reason for switching tasks');
      return;
    }
    
    try {
      // Start the new assignment (backend will auto-pause current one)
      await api.post(`/work-assignments/${showSwitchConfirm.assignment.id}/start`, {
        reason: showSwitchConfirm.reason.trim()
      });
      
      // Refresh assignments and switch current assignment
      await checkActiveAssignment();
      setCurrentAssignment(showSwitchConfirm.assignment);
      setShowSwitchConfirm(null);
    } catch (error: any) {
      console.error('Error switching assignment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to switch task';
      alert(errorMessage);
    }
  };

  const getTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  const handleDownload = async (itineraryId: number) => {
    try {
      const { itineraryService } = await import('../../services/itinerary');
      await itineraryService.downloadItinerary(itineraryId);
    } catch (error) {
      console.error('Error downloading itinerary:', error);
      alert('Failed to download itinerary');
    }
  };

  const handleRefresh = () => {
    checkActiveAssignment();
  };

  const calculateDuration = () => {
    if (!currentAssignment?.started_at) return '';
    
    // Parse the UTC timestamp from database
    const start = new Date(currentAssignment.started_at + 'Z'); // Force UTC parsing
    const now = new Date(); // Current time in UTC
    const diffMs = now.getTime() - start.getTime();
    
    // Ensure positive duration
    if (diffMs < 0) return '0m';
    
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours === 0) {
      return `${minutes}m`;
    }
    return `${hours}h ${minutes}m`;
  };

  const isOverdue = () => {
    if (!currentAssignment?.started_at || currentAssignment?.status === 'paused') return false;
    const start = new Date(currentAssignment.started_at + 'Z');
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    return totalMinutes > 30;
  };

  if (activeAssignments.length === 0) return null;

  return (
    <>
      {/* Floating Widget */}
      <div className={`fixed top-4 right-4 z-50 transition-all duration-300 ${isMinimized ? 'w-12 h-12' : 'w-80'}`}>
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {isMinimized ? (
            <button
              onClick={() => setIsMinimized(false)}
              className="w-full h-full flex items-center justify-center bg-blue-500 text-white hover:bg-blue-600"
            >
              <Clock className="h-5 w-5" />
            </button>
          ) : (
            <>
              <div className={`bg-gradient-to-r text-white p-3 ${
                currentAssignment?.status === 'paused' 
                  ? 'from-yellow-500 to-orange-600' 
                  : 'from-blue-500 to-indigo-600'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {currentAssignment?.status === 'paused' ? (
                      <>
                        <span className="text-lg">⏸️</span>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">Paused</span>
                          {currentAssignment?.pause_reason && (
                            <span className="text-xs opacity-90">{currentAssignment.pause_reason}</span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <Clock className="h-4 w-4" />
                        <span className="text-sm font-medium">Working on</span>
                      </>
                    )}
                    {activeAssignments.length > 1 && (
                      <span className="bg-white bg-opacity-20 px-2 py-1 rounded-full text-xs">
                        {activeAssignments.length} tasks
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={handleRefresh}
                      className="text-white hover:text-gray-200 p-1"
                      title="Refresh"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setIsMinimized(true)}
                      className="text-white hover:text-gray-200 p-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-1">
                  <div className="font-semibold text-sm">{currentAssignment?.agency_name}</div>
                  <div className="text-xs opacity-90 flex items-center justify-between">
                    <span>
                      {calculateDuration()}
                      {isOverdue() && <span className="ml-1 text-red-200">⚠️ Overdue</span>}
                    </span>
                    {lastUpdated > 0 && (
                      <span className="text-xs opacity-75">Updated {getTimeAgo(lastUpdated)}</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="p-3">
                <div className="text-xs text-gray-600 mb-2">
                  Travel: {currentAssignment && new Date(currentAssignment.travel_date).toLocaleDateString('en-GB')}
                  {currentAssignment && (
                    <span className="ml-2 text-blue-600">• Enquiry #{currentAssignment.enquiry_id}</span>
                  )}
                  {currentAssignment?.status && (
                    <span className="ml-2 text-red-600 font-bold">• Status: {currentAssignment.status}</span>
                  )}
                </div>
                <div className="text-sm text-gray-800 mb-3 line-clamp-2">
                  {currentAssignment?.plan_text}
                </div>
                
                {/* Assignment List */}
                {showAssignmentList && activeAssignments.length > 1 && (
                  <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                    <div className="text-xs font-medium text-gray-700 mb-2">Switch Task:</div>
                    {activeAssignments.map((assignment) => (
                      <button
                        key={assignment.id}
                        onClick={() => handleSwitchAssignment(assignment)}
                        className={`block w-full text-left p-2 rounded text-xs mb-1 ${
                          currentAssignment?.id === assignment.id 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        {assignment.agency_name}
                        {currentAssignment?.id === assignment.id && (
                          <span className="text-xs text-blue-600 ml-1">(current)</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                
                <div className="flex space-x-2 mb-3">
                  <button
                    onClick={() => setShowPreview(true)}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-200 text-xs font-medium flex items-center justify-center"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Preview
                  </button>
                  
                  {currentAssignment?.status === 'paused' ? (
                    <button
                      onClick={handleResumeWork}
                      className="flex-1 bg-green-100 text-green-700 py-2 px-3 rounded-lg hover:bg-green-200 text-xs font-medium flex items-center justify-center"
                    >
                      ▶️ Resume
                    </button>
                  ) : (
                    <button
                      onClick={handlePauseWork}
                      className="flex-1 bg-yellow-100 text-yellow-700 py-2 px-3 rounded-lg hover:bg-yellow-200 text-xs font-medium flex items-center justify-center"
                    >
                      ⏸️ Pause
                    </button>
                  )}
                  
                  {activeAssignments.length > 1 && (
                    <button
                      onClick={() => setShowAssignmentList(!showAssignmentList)}
                      className="flex-1 bg-blue-100 text-blue-700 py-2 px-3 rounded-lg hover:bg-blue-200 text-xs font-medium flex items-center justify-center"
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      Switch
                    </button>
                  )}
                </div>
                
                <button
                  onClick={handleCompleteWork}
                  disabled={currentAssignment?.status === 'paused'}
                  className={`w-full py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center ${
                    currentAssignment?.status === 'paused'
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {currentAssignment?.status === 'paused' ? 'Paused - Resume to Complete' : 'Complete Work'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Pause Modal */}
      {showPauseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-55">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Pause Assignment</h2>
              <p className="text-gray-600 mb-4">
                You're about to pause work on <strong>{currentAssignment?.agency_name}</strong>.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-600 mb-2">Reason for pausing:</label>
                <textarea
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  placeholder="e.g., Waiting for client info, need hotel confirmation, checking with team lead..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowPauseModal(false);
                    setPauseReason('');
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmPause}
                  className="flex-1 bg-yellow-600 text-white py-2 px-4 rounded-lg hover:bg-yellow-700"
                >
                  Pause Work
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Switch Confirmation Modal */}
      {showSwitchConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-55">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Switch Task</h2>
              <p className="text-gray-600 mb-4">
                You're currently working on <strong>{currentAssignment?.agency_name}</strong>. 
                Switch to <strong>{showSwitchConfirm.assignment.agency_name}</strong>?
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Reason for switching <span className="text-red-500">*</span>:
                </label>
                <textarea
                  value={showSwitchConfirm.reason}
                  onChange={(e) => setShowSwitchConfirm({...showSwitchConfirm, reason: e.target.value})}
                  placeholder="e.g., Waiting for client info, need to check something..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                  rows={3}
                  required
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowSwitchConfirm(null)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSwitch}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
                >
                  Switch Task
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && currentAssignment && (
        <EnquiryDetailsModal
          isOpen={true}
          enquiry={currentAssignment}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Assignment Completion Wizard */}
      {showCompletionWizard && currentAssignment && (
        <AssignmentCompletionWizard
          assignment={currentAssignment}
          availableItineraries={availableItineraries}
          onClose={() => setShowCompletionWizard(false)}
          onSuccess={() => {
            // Clear API cache to ensure fresh data
            apiCache.clear('active-assignments');
            checkActiveAssignment();
            setShowCompletionWizard(false);
          }}
        />
      )}
    </>
  );
};

export default WorkWidget;